import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Rating,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingCart as ShoppingCartIcon,
  Message as MessageIcon,
  Share as ShareIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DynamicSEO from '../components/DynamicSEO';
import { generateServiceSEO } from '../lib/seoUtils';
import { 
  getServiceById, 
  orderService,
  incrementServiceViews,
  ServiceWithDetails,
} from '../lib/api/services';
import { getCurrentUserProfile } from '../lib/api/profile';
import { hasServiceAccess, getServiceProviderInfo } from '../lib/api/projectAccess';
import ServiceAccessModal from '../components/ServiceAccessModal';
import ServiceContactModal from '../components/ServiceContactModal';

const ServiceDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [service, setService] = useState<ServiceWithDetails | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isOwnService, setIsOwnService] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [hasProviderAccess, setHasProviderAccess] = useState(false);
  const [providerInfo, setProviderInfo] = useState<any>(null);
  const [serviceAccessModal, setServiceAccessModal] = useState<{
    open: boolean;
    serviceId: string;
    serviceTitle: string;
  }>({ open: false, serviceId: '', serviceTitle: '' });
  const [serviceContactModal, setServiceContactModal] = useState<{
    open: boolean;
    serviceId: string;
    serviceTitle: string;
  }>({ open: false, serviceId: '', serviceTitle: '' });
  
  // Order dialog state
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderData, setOrderData] = useState({
    title: '',
    description: '',
    requirements: '',
  });

  // Load service data
  useEffect(() => {
    if (id) {
      loadServiceData();
    }
  }, [id]);

  const loadServiceData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load service details
      const serviceData = await getServiceById(id);
      if (!serviceData) {
        setError('Услугата не е намерена');
        return;
      }
      
      setService(serviceData);
      
      // Increment views
      await incrementServiceViews(id);
      
      // Check user role and ownership (only if user is authenticated)
      try {
        const userProfile = await getCurrentUserProfile();
        if (userProfile) {
          setUserRole(userProfile.role);
          setIsOwnService(userProfile.id === serviceData.provider_id);
          
          // Check if user has access to provider details
          const access = await hasServiceAccess(id);
          setHasProviderAccess(access);
          
          if (access) {
            // Load provider info if user has access
            try {
              const providerData = await getServiceProviderInfo(id);
              setProviderInfo(providerData);
            } catch (err) {
              console.error('Error loading provider info:', err);
            }
          }
        }
      } catch (authError) {
        // User is not authenticated, continue without user data
        console.log('User not authenticated, showing public view');
      }
      
    } catch (err) {
      console.error('Error loading service:', err);
      setError('Грешка при зареждане на услугата');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceAccessClick = () => {
    if (!service) return;
    
    setServiceAccessModal({
      open: true,
      serviceId: service.id,
      serviceTitle: service.title
    });
  };

  const handleServiceAccessGranted = () => {
    // Reload service data to get provider access
    loadServiceData();
  };

  const handleContactProviderClick = () => {
    if (!service) return;
    
    setServiceContactModal({
      open: true,
      serviceId: service.id,
      serviceTitle: service.title
    });
  };


  const handleOrderService = async () => {
    if (!service || !orderData.title.trim() || !orderData.description.trim()) {
      setError('Моля, попълнете всички задължителни полета');
      return;
    }

    try {
      setOrderLoading(true);
      setError(null);
      
      await orderService({
        service_id: service.id,
        title: orderData.title.trim(),
        description: orderData.description.trim(),
        requirements: orderData.requirements.trim() || undefined,
      });
      
      setOrderDialogOpen(false);
      setOrderData({ title: '', description: '', requirements: '' });
      
      // Show success message or redirect
      navigate('/profile?tab=orders');
      
    } catch (err) {
      console.error('Error ordering service:', err);
      setError('Грешка при поръчване на услугата');
    } finally {
      setOrderLoading(false);
    }
  };

  const toggleFavorite = () => {
    if (!service) return;
    
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(service.id)) {
        newFavorites.delete(service.id);
      } else {
        newFavorites.add(service.id);
      }
      return newFavorites;
    });
  };

  const formatPrice = (price: number, priceType: string) => {
    const priceStr = price.toLocaleString();
    switch (priceType) {
      case 'HOURLY': return `€${priceStr}/час`;
      case 'DAILY': return `€${priceStr}/ден`;
      case 'WEEKLY': return `€${priceStr}/седмица`;
      case 'MONTHLY': return `€${priceStr}/месец`;
      default: return `€${priceStr}`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('bg-BG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (error || !service) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Container maxWidth="md" sx={{ py: 8, position: 'relative', zIndex: 1 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 4,
              p: 4,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" sx={{ color: '#ef4444', mb: 2 }}>
              {error || 'Услугата не е намерена'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/find-services')}
              sx={{
                background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                color: '#ffffff',
              }}
            >
              Назад към услугите
            </Button>
          </Card>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#0f0f23', 
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {service && (
        <DynamicSEO 
          seoData={generateServiceSEO(service, i18n.language)} 
        />
      )}
      {/* Animated background shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
          borderRadius: '50%',
          opacity: 0.3,
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: 40,
          height: 40,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
          borderRadius: '20%',
          opacity: 0.4,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Header />
      
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate('/find-services')}
              sx={{ color: '#28A0F0', mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <BusinessIcon sx={{ color: '#28A0F0', mr: 1, fontSize: '2rem' }} />
            <Typography 
              variant="h3" 
              sx={{ 
                color: '#ffffff', 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {service.title}
            </Typography>
          </Box>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} lg={8}>
            {/* Service Details */}
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 4,
                backdropFilter: 'blur(10px)',
                mb: 4,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                  <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, flex: 1 }}>
                    {service.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={t('findServices.addToFavorites')}>
                      <IconButton
                        onClick={toggleFavorite}
                        sx={{ color: favorites.has(service.id) ? '#ef4444' : '#a0a0a0' }}
                      >
                        {favorites.has(service.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share">
                      <IconButton sx={{ color: '#a0a0a0' }}>
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Report">
                      <IconButton sx={{ color: '#a0a0a0' }}>
                        <WarningIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Typography variant="body1" sx={{ color: '#d0d0d0', mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  {service.description}
                </Typography>

                {/* Skills */}
                {service.skills.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, display: 'flex', alignItems: 'center' }}>
                      <CodeIcon sx={{ mr: 1, color: '#28A0F0' }} />
                      Умения
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {service.skills.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          sx={{
                            backgroundColor: 'rgba(40, 160, 240, 0.2)',
                            color: '#28A0F0',
                            border: '1px solid rgba(40, 160, 240, 0.3)',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Features */}
                {service.features.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon sx={{ mr: 1, color: '#28A0F0' }} />
                      Включвания
                    </Typography>
                    <Stack spacing={1}>
                      {service.features.map((feature, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon 
                            sx={{ 
                              color: feature.included ? '#10b981' : '#a0a0a0',
                              fontSize: '1.2rem'
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: feature.included ? '#ffffff' : '#a0a0a0',
                              textDecoration: feature.included ? 'none' : 'line-through'
                            }}
                          >
                            {feature.name}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Reviews Section */}
            {service.reviews && service.reviews.length > 0 && (
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: 4,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ color: '#ffffff', mb: 3, display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ mr: 1, color: '#fbbf24' }} />
                    Отзиви ({service.reviews.length})
                  </Typography>
                  
                  <Stack spacing={3}>
                    {service.reviews.map((review) => (
                      <Box key={review.id} sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar 
                            src={review.client.avatar_url || undefined} 
                            sx={{ mr: 2, width: 40, height: 40 }}
                          >
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 500 }}>
                              {review.client.full_name || 'Анонимен'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                              {formatDate(review.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>Качество:</Typography>
                            <Rating value={review.rating_quality} size="small" readOnly />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>Комуникация:</Typography>
                            <Rating value={review.rating_communication} size="small" readOnly />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>Доставка:</Typography>
                            <Rating value={review.rating_delivery} size="small" readOnly />
                          </Box>
                        </Box>
                        
                        {review.comment && (
                          <Typography variant="body2" sx={{ color: '#d0d0d0', mt: 1 }}>
                            {review.comment}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            {/* Order Card */}
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)',
                border: '1px solid rgba(40, 160, 240, 0.3)',
                borderRadius: 4,
                backdropFilter: 'blur(10px)',
                mb: 4,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h3" sx={{ color: '#28A0F0', fontWeight: 800, mb: 1 }}>
                    {formatPrice(service.price, service.price_type)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                    {service.price_type === 'FIXED' ? 'Фиксирана цена' : 
                     service.price_type === 'HOURLY' ? 'На час' :
                     service.price_type === 'DAILY' ? 'На ден' :
                     service.price_type === 'WEEKLY' ? 'На седмица' : 'На месец'}
                  </Typography>
                </Box>

                <Stack spacing={2} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon sx={{ color: '#28A0F0' }} />
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      Срок за доставка: {service.delivery_days} дни
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#28A0F0' }} />
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      Ревизии: {service.revisions}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VisibilityIcon sx={{ color: '#28A0F0' }} />
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      Гледания: {service.views_count}
                    </Typography>
                  </Box>
                </Stack>

                {!isOwnService && (
                  <Stack spacing={2}>
                    {userRole === 'CLIENT' && (
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        startIcon={<ShoppingCartIcon />}
                        onClick={() => setOrderDialogOpen(true)}
                        sx={{
                          background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                          color: '#ffffff',
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                          },
                        }}
                      >
                        Поръчай услугата
                      </Button>
                    )}
                    
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<MessageIcon />}
                      onClick={hasProviderAccess ? handleContactProviderClick : handleServiceAccessClick}
                      sx={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#ffffff',
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        },
                      }}
                    >
                      {hasProviderAccess ? 'Изпрати имейл' : 'Изпрати имейл (2 кредита)'}
                    </Button>
                  </Stack>
                )}

                {isOwnService && (
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: '#ffffff',
                      py: 1.5,
                    }}
                  >
                    Редактирай услугата
                  </Button>
                )}
              </CardContent>
            </Card>

          </Grid>
        </Grid>

        {/* Order Dialog */}
        <Dialog
          open={orderDialogOpen}
          onClose={() => setOrderDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#1a1a2e',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            Поръчай услуга
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Order Title"
                value={orderData.title}
                onChange={(e) => setOrderData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Brief description of what you want"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Order Description"
                value={orderData.description}
                onChange={(e) => setOrderData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe in detail what you want to be done..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Requirements (Optional)"
                value={orderData.requirements}
                onChange={(e) => setOrderData(prev => ({ ...prev, requirements: e.target.value }))}
                placeholder="Add special requirements, preferences or details..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#ffffff',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputLabel-root': { color: '#a0a0a0' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Button
              onClick={() => setOrderDialogOpen(false)}
              sx={{ color: '#a0a0a0' }}
            >
              Отказ
            </Button>
            <Button
              variant="contained"
              onClick={handleOrderService}
              disabled={orderLoading}
              startIcon={orderLoading ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />}
              sx={{
                background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                color: '#ffffff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                },
              }}
            >
              {orderLoading ? 'Поръчване...' : 'Поръчай'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Footer />

      {/* Service Access Modal */}
      <ServiceAccessModal
        open={serviceAccessModal.open}
        onClose={() => setServiceAccessModal({ open: false, serviceId: '', serviceTitle: '' })}
        serviceId={serviceAccessModal.serviceId}
        serviceTitle={serviceAccessModal.serviceTitle}
        onAccessGranted={handleServiceAccessGranted}
      />

      {/* Service Contact Modal */}
      <ServiceContactModal
        open={serviceContactModal.open}
        onClose={() => setServiceContactModal({ open: false, serviceId: '', serviceTitle: '' })}
        serviceId={serviceContactModal.serviceId}
        serviceTitle={serviceContactModal.serviceTitle}
        providerInfo={providerInfo}
      />

    </Box>
  );
};

export default ServiceDetailsPage;
